import path from "node:path";
import { fetchSchema, resolveApp, writeSchema } from "../lib/sync-schema.js";

interface PullSchemaOptions {
  from?: string;
  out?: string;
  force?: boolean;
  org?: string;
}

/**
 * `clawnify pull schema --from <app-id-or-slug>`
 *
 * Fetch the deployed app's current schema.sql snapshot and write it to
 * the local working directory. Used to back-port a deployed instance's
 * evolved schema into an open-source template repo, or to refresh a
 * developer's local copy after agent-driven schema changes.
 *
 * Refuses to overwrite a locally-modified schema.sql unless --force is
 * passed. Refusal is git-aware: if the local file has uncommitted
 * changes (or we're outside git and the file differs), we bail with a
 * helpful message instead of silently clobbering work.
 */
export async function pullSchemaCommand(options: PullSchemaOptions): Promise<void> {
  if (!options.from) {
    console.error("Usage: clawnify pull schema --from <app-id-or-slug>");
    process.exit(64);
  }

  const ref = await resolveApp(options.from, options.org);
  if (!ref) {
    console.error(`App '${options.from}' not found in your orgs.`);
    process.exit(1);
  }

  let schema: string | null;
  try {
    schema = await fetchSchema(ref);
  } catch (err) {
    console.error(`Failed to fetch schema: ${(err as Error).message}`);
    process.exit(1);
  }

  const targetPath = path.resolve(options.out ?? "schema.sql");

  const result = await writeSchema(targetPath, schema, { force: options.force });
  const rel = path.relative(process.cwd(), result.path) || result.path;

  switch (result.status) {
    case "no-schema":
      console.log(`  ${ref.slug}: no schema.sql in R2 (pre-backfill app, or no database)`);
      process.exit(0);

    case "unchanged":
      console.log(`  ${rel} is already up to date`);
      process.exit(0);

    case "written":
      console.log(`  ✓ wrote ${rel} (${result.bytes} bytes, from ${ref.slug})`);
      process.exit(0);

    case "refused-uncommitted":
      console.error(
        `  ${rel} has uncommitted changes — refusing to overwrite.\n` +
          `  Commit/stash first, or rerun with --force.`,
      );
      process.exit(1);

    case "refused-different":
      console.error(
        `  ${rel} exists locally and differs from the deployed copy.\n` +
          `  Not inside a git repo — can't tell intent. Rerun with --force to overwrite.`,
      );
      process.exit(1);
  }
}
